import { useCallback, useMemo } from "react";
import { useQuery, useMutation } from "@apollo/client/react";
import {
  GetContractsDocument,
  GetContractDocument,
  UpdateContractsDocument,
  DeleteContractsDocument,
  type GetContractsQuery,
  type GetContractQuery,
  type UpdateContractsMutation,
  type DeleteContractsMutation,
  type Contracts,
  type ContractsUpdateInput,
} from "@/src/gql/graphql";
import type { EntityHooks } from "@/components/shared/UniversalCRMTable/types/shared";
import { usePaginatedCollection } from "@/lib/hooks/api/shared/use-paginated-collection";
import {
  EMPTY_INFINITE_RESULT,
  useStableMutationResult,
} from "@/lib/hooks/api/shared/stable-entity-hook-results";
import { serializeGraphqlInput } from "@/lib/graphql/serialize-input";
// Writes go through pg_graphql, not an API route, so the server-side
// mapToUserFacingError never sees them. See mutation-error.ts.
import { crmMutationError } from "@/lib/utils/crm/mutation-error";

/**
 * Contracts are READ-mostly: a contract is a real e-signature document created
 * by the dedicated contract send flow, never by the generic CRM create form.
 * So `useCreate` throws; `useUpdate` (manual edits — link to a deal, status
 * correction) and `useDelete` (admin) are supported.
 *
 * Apollo + usePaginatedCollection (cache-and-network) + serializeGraphqlInput,
 * mirroring `contacts-graphql.ts`. The ContractFields fragment selects every
 * displayed column, so the codegen result node carries fragment refs — cast the
 * resolved node to `Contracts` at the read boundary.
 */

/** Drop Apollo cache metadata (`__typename`, fragment-ref keys) before a mutation. */
function cleanContractUpdateInput(input: Partial<Contracts>): ContractsUpdateInput {
  const entries = Object.entries(input).filter(([key]) => !key.startsWith("__") && key !== "id");
  return Object.fromEntries(entries);
}

/**
 * Module-level so `usePaginatedCollection`'s result memo has stable deps.
 */
const selectContractsConnection = (data: GetContractsQuery) => data.contractsCollection;
const mapContractNode = (node: unknown): Contracts => node as Contracts;

/**
 * `useCreate` is a constant rejection (contracts are created by the e-signature
 * send flow), so it is allocated once rather than per render.
 */
const CONTRACT_CREATE_UNSUPPORTED = {
  mutateAsync: async (): Promise<Contracts> => {
    throw new Error(
      "Contracts are created via the contract send flow, not the generic form"
    );
  },
  isPending: false,
};

/**
 * Allocated ONCE — the object is a dependency of `useBuildActionContext`'s memo
 * and closes over nothing render-scoped (see `contacts-graphql.ts`).
 */
const CONTRACT_ENTITY_HOOKS: EntityHooks<Contracts> = {
  queryDocument: GetContractsDocument,

  usePaginated: (params?: Record<string, unknown>) =>
    // Canonical list-query path (cache-and-network) — see use-paginated-collection.ts.
    usePaginatedCollection(
      GetContractsDocument,
      params,
      selectContractsConnection,
      mapContractNode
    ),

  useInfinite: () => EMPTY_INFINITE_RESULT,

  useSingle: (id: string, options?: { enabled?: boolean }) => {
    const result = useQuery<GetContractQuery>(GetContractDocument, {
      variables: { id },
      errorPolicy: "all",
      skip: !id || (options && options.enabled === false),
    });
    return useMemo(() => {
      const node = result.data?.contractsCollection?.edges?.[0]?.node;
      return {
        data: node ? (node as unknown as Contracts) : undefined,
        isLoading: result.loading,
        error: result.error || null,
        refetch: result.refetch,
      };
    }, [result]);
  },

  useUpdate: () => {
    const [mutate, result] = useMutation<UpdateContractsMutation>(UpdateContractsDocument);

    const mutateAsync = useCallback(
      async ({ id, ...input }: { id: string } & Partial<Contracts>) => {
        const cleanInput = serializeGraphqlInput(
          "contracts",
          cleanContractUpdateInput(input)
        );
        const { data, error } = await mutate({
          variables: { id, input: cleanInput },
          errorPolicy: "all",
        });

        if (error) throw crmMutationError(error, "Couldn't update the contract.");

        const updated = data?.updatecontractsCollection?.records?.[0];
        if (!updated) throw new Error("Couldn't update the contract.");
        return updated as unknown as Contracts;
      },
      [mutate]
    );

    return useStableMutationResult(mutateAsync, result.loading);
  },

  useDelete: () => {
    const [mutate, result] = useMutation<DeleteContractsMutation>(DeleteContractsDocument);
    const mutateAsync = useCallback(
      async (id: string) => {
        const { data, error } = await mutate({
          variables: { id },
          errorPolicy: "all",
        });
        if (error) throw crmMutationError(error, "Couldn't delete the contract.");
        const deletedId = data?.deleteFromcontractsCollection?.records?.[0]?.id;
        if (!deletedId) throw new Error("Couldn't delete the contract.");
        return deletedId;
      },
      [mutate]
    );
    return useStableMutationResult(mutateAsync, result.loading);
  },

  useCreate: () => CONTRACT_CREATE_UNSUPPORTED,
};

export const useContractHooks = (): EntityHooks<Contracts> => CONTRACT_ENTITY_HOOKS;
